import FormControl from '@material-ui/core/FormControl'
import IconButton from '@material-ui/core/IconButton'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import Modal from '@material-ui/core/Modal'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import {
  makeStyles,
  Theme,
  createStyles,
  createGenerateClassName,
  StylesProvider,
} from '@material-ui/core/styles'
import BackIcon from '@material-ui/icons/ArrowBack'
import PersonIcon from '@material-ui/icons/Person'
import SearchIcon from '@material-ui/icons/Search'
import CartIcon from '@material-ui/icons/ShoppingCart'
import React, { Fragment, Suspense, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'

import { useRegion } from './RegionChooser/hook'
import RegionChooserButton from './RegionChooserButton'

declare const SI_LOGGED_IN: boolean
declare const SI_LOGOUT_URL: string

const useStyles = makeStyles((theme: Theme) => {
  const modalHeight = 55

  return createStyles({
    button: {
      padding: 12,
    },
    icon: {
      cursor: 'pointer',
      width: 12,
      maxHeight: 12,
      marginTop: 1,
      marginLeft: theme.spacing(1),
    },
    modal: {
      position: 'absolute',
      backgroundColor: 'white',
      top: 2,
      width: '100%',
      height: modalHeight,
    },
    form: {
      display: 'inline-flex',
    },
    input: {
      margin: theme.spacing(1),
      height: modalHeight - theme.spacing(2),
    },
  })
})

export interface IProps {
  showSearch: boolean
  showAccount: boolean
}

const HeaderButtonBar = ({ showSearch, showAccount }: IProps) => {
  const classes = useStyles()

  const { t } = useTranslation('Header', {})
  const { t: tSearch } = useTranslation('Searchbar', {})
  const { ready: rReady, cartPath, path } = useRegion()
  const [accountMenuEl, setAccountMenuEl] = useState<null | HTMLElement>(null)
  const [searchModalOpen, setSearchModalOpen] = useState(false)
  const [searchPath, setSearchPath] = useState('/')

  useEffect(() => {
    if (!rReady) {
      return
    }
    setSearchPath(path)
  }, [rReady])

  if (!rReady) {
    return null
  }

  const searchButton = (
    <IconButton
      className={classes.button}
      aria-label={t('search-open')}
      title={t('search-open')}
      onClick={() => setSearchModalOpen(true)}
    >
      <SearchIcon />
    </IconButton>
  )

  const accountButton = (
    <IconButton
      className={classes.button}
      aria-label={t('account-menu-open')}
      title={t('account-menu-open')}
      onClick={(evt) => setAccountMenuEl(evt.currentTarget)}
    >
      <PersonIcon />
    </IconButton>
  )

  const searchModalBody: any = (
    <div className={classes.modal}>
      {/* <div className={'cancel-button'}>
        <button
          className={'material-icons mdc-icon-button'}
          aria-label="Close"
          aria-pressed="false"
          onClick={() => setSearchModalOpen(false)}
        >
          cancel
        </button>
      </div> */}

      <IconButton
        aria-label={t('search-close')}
        title={t('search-close')}
        onClick={() => setSearchModalOpen(false)}
      >
        <BackIcon />
      </IconButton>

      <form method="get" action={searchPath} className={classes.form}>
        <FormControl variant="standard">
          <OutlinedInput
            name="s"
            required
            type="text"
            labelWidth={0}
            placeholder={tSearch('placeholder')}
            className={classes.input}
          />
        </FormControl>

        <IconButton
          type="submit"
          aria-label={tSearch('button')}
          title={tSearch('button')}
        >
          <SearchIcon />
        </IconButton>
      </form>
    </div>
  )

  return (
    <Suspense fallback={<div></div>}>
      <Modal
        open={searchModalOpen}
        onClose={() => {
          setSearchModalOpen(false)
        }}
      >
        {searchModalBody}
      </Modal>
      {showSearch ? searchButton : null}
      {showAccount ? accountButton : null}
      <Menu
        id="account-menu"
        anchorEl={accountMenuEl}
        getContentAnchorEl={null}
        keepMounted={false}
        open={Boolean(accountMenuEl)}
        onClose={() => setAccountMenuEl(null)}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {SI_LOGGED_IN ? (
          <Fragment>
            <a href="/myaccount/#/">
              <MenuItem>{t('account-menu-my-account')}</MenuItem>
            </a>
            <a href="/myaccount/#/orders">
              <MenuItem>{t('account-menu-order-history')}</MenuItem>
            </a>
          </Fragment>
        ) : null}

        {SI_LOGGED_IN ? (
          <a href="/myaccount/#/logout">
            <MenuItem>{t('account-menu-logout')}</MenuItem>
          </a>
        ) : (
          <a href="/myaccount/">
            <MenuItem>{t('account-menu-login')}</MenuItem>
          </a>
        )}
      </Menu>

      <IconButton
        className={classes.button}
        aria-label={t('shopping-cart-open')}
        title={t('shopping-cart-open')}
        href={cartPath ?? '/cart'}
      >
        <CartIcon />
      </IconButton>
      <RegionChooserButton />
    </Suspense>
  )
}

export default (element: Element) => {
  const showSearch = element.getAttribute('data-search') === 'true'
  const showAccount = element.getAttribute('data-account') === 'true'

  const generateClassName = createGenerateClassName({
    disableGlobal: true,
    productionPrefix: 'headerButtonBarCoreJss',
    seed: 'headerButtonBar',
  })

  try {
    ReactDOM.render(
      <StylesProvider generateClassName={generateClassName}>
        <Suspense fallback={<div></div>}>
          <HeaderButtonBar showAccount={showAccount} showSearch={showSearch} />
        </Suspense>
      </StylesProvider>,
      element
    )
  } catch (err) {
    console.error(err)
  }
}
